import { Action } from 'Models/Actions';
import { ExceptionEntity } from 'Models/Exceptions';
import { FolderVersion } from 'Models/Folders';
import { AssessmentState, OperationalControl } from 'Models/OperationalControls';
import { ThirdParty, ThirdPartyService, TprmDueDiligenceAssessment } from 'Models/TPRM';
import { Business, UcrmDueDiligenceAssessment, UseCase } from 'Models/Ucrm';

import { Issue } from './Issues';

export enum NotificationType {
    ACTION_DUE = 'Action Due',
    CONTROL_ASSESSMENT = 'Control Assessment',
    EXCEPTION_EXPIRY = 'Exception Expiry',
    ISSUE_DUE = 'Issue Due',
    /** UCRM */
    UCRM_DUE_DILIGENCE_WORKFLOW_DUE = 'AI Governance Due Diligence Workflow Due',
    UCRM_FOLDER_EXPIRY = 'AI Governance Folder Expiry',
    UCRM_IRQ_DUE = 'AI Governance Inherent Risk Questionnaire Due',
    UCRM_DUE_DILIGENCE_ASSESSMENT_DUE = 'AI Governance Use Case Assessment Due',
    /** TPRM */
    TPRM_DUE_DILIGENCE_WORKFLOW_DUE = 'Due Diligence Workflow Due',
    TPRM_FOLDER_EXPIRY = 'Folder Expiry',
    TPRM_IRQ_DUE = 'Inherent Risk Questionnaire Due',
    TPRM_DUE_DILIGENCE_ASSESSMENT_DUE = 'Third-Party Assessment Due', // TODO: Change the value to "Due Diligence Assessment Due". Is this still a good idea? Check if a change also needs to be made to UCRM_DUE_DILIGENCE_ASSESSMENT_DUE. Values are stored on notifications, so this requires a data migration.
}

/**
 * This defines common properties that all Notification types have.
 */
interface UserNotificationBase {
    due_date: string;
    identifier: string;
}

export interface ActionDueNotification extends UserNotificationBase {
    _type: NotificationType.ACTION_DUE;
    action: Action;
}

export interface TprmDueDiligenceAssessmentDueNotification extends UserNotificationBase {
    _type: NotificationType.TPRM_DUE_DILIGENCE_ASSESSMENT_DUE;
    assessment: TprmDueDiligenceAssessment;
    third_party_service: ThirdPartyService;
}

export interface ControlAssessmentDueNotification extends UserNotificationBase {
    _type: NotificationType.CONTROL_ASSESSMENT;
    control: OperationalControl;
    status: AssessmentState;
}

export interface ExceptionExpiryNotification extends UserNotificationBase {
    _type: NotificationType.EXCEPTION_EXPIRY;
    exception: ExceptionEntity;
}

export interface TprmFolderExpiryNotification extends UserNotificationBase {
    _type: NotificationType.TPRM_FOLDER_EXPIRY;
    folder: FolderVersion;
    third_party: ThirdParty;
}

export interface TprmIrqDueNotification extends UserNotificationBase {
    _type: NotificationType.TPRM_IRQ_DUE;
    third_party_service: ThirdPartyService;
}

export interface IssueDueNotification extends UserNotificationBase {
    _type: NotificationType.ISSUE_DUE;
    issue: Issue;
}

export interface TprmDueDiligenceWorkflowDueNotification extends UserNotificationBase {
    _type: NotificationType.TPRM_DUE_DILIGENCE_WORKFLOW_DUE;
    third_party_service: ThirdPartyService;
}

export interface UcrmFolderExpiryNotification extends UserNotificationBase {
    _type: NotificationType.UCRM_FOLDER_EXPIRY;
    business: Business;
    folder: FolderVersion;
}

export interface UcrmIrqDueNotification extends UserNotificationBase {
    _type: NotificationType.UCRM_IRQ_DUE;
    use_case: UseCase;
}

export interface UcrmDueDiligenceAssessmentDueNotification extends UserNotificationBase {
    _type: NotificationType.UCRM_DUE_DILIGENCE_ASSESSMENT_DUE;
    assessment: UcrmDueDiligenceAssessment;
    use_case: UseCase;
}

export interface UcrmDueDiligenceWorkflowDueNotification extends UserNotificationBase {
    _type: NotificationType.UCRM_DUE_DILIGENCE_WORKFLOW_DUE;
    use_case: UseCase;
}

export type UserNotification = ActionDueNotification | ControlAssessmentDueNotification | ExceptionExpiryNotification | IssueDueNotification | TprmDueDiligenceAssessmentDueNotification | TprmDueDiligenceWorkflowDueNotification | TprmFolderExpiryNotification | TprmIrqDueNotification | UcrmDueDiligenceAssessmentDueNotification | UcrmDueDiligenceWorkflowDueNotification | UcrmFolderExpiryNotification | UcrmIrqDueNotification;
